import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Anchor, ArrowLeft, Shield, Users, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem('admin_token');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUsers(res.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load users');
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId, verified) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/verify?verified=${verified}`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`User ${verified ? 'verified' : 'unverified'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleBanUser = async (userId, banned) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/ban?banned=${banned}`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`User ${banned ? 'banned' : 'unbanned'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 w-full backdrop-blur-lg bg-background/80 border-b border-border/40 h-16 flex items-center px-4 justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full"
            onClick={() => navigate('/')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold" data-testid="admin-title">Admin Dashboard</span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          Logout
        </Button>
      </header>

      <div className="max-w-4xl mx-auto w-full p-4">
        <div className="bg-card border border-white/5 rounded-xl p-6 mb-6" data-testid="users-section">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-heading font-semibold" data-testid="users-title">Users</h2>
            <Badge variant="secondary" data-testid="users-count">{users.length}</Badge>
          </div>

          <div className="space-y-3">
            {users.map((user) => (
              <div
                key={user.id}
                className="bg-card/50 border border-white/5 rounded-lg p-4"
                data-testid={`user-${user.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="font-medium" data-testid={`user-email-${user.id}`}>{user.email}</p>
                      {user.is_verified && (
                        <Badge className="bg-primary/20 text-primary border-primary/30" data-testid={`verified-badge-${user.id}`}>
                          Verified
                        </Badge>
                      )}
                      {user.is_banned && (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30" data-testid={`banned-badge-${user.id}`}>
                          Banned
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                      <div>
                        <span className="text-xs">Name:</span> {user.name || 'Not set'}
                      </div>
                      <div>
                        <span className="text-xs">Rank:</span> {user.rank || 'Not set'}
                      </div>
                      <div>
                        <span className="text-xs">Experience:</span> {user.years_experience || 'Not set'} years
                      </div>
                      <div>
                        <span className="text-xs">Joined:</span> {format(new Date(user.created_at), 'MMM d, yyyy')}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {user.is_verified ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-full"
                        onClick={() => handleVerifyUser(user.id, false)}
                        data-testid={`unverify-button-${user.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Unverify
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-full"
                        onClick={() => handleVerifyUser(user.id, true)}
                        data-testid={`verify-button-${user.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Verify
                      </Button>
                    )}
                    {user.is_banned ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-full text-green-400"
                        onClick={() => handleBanUser(user.id, false)}
                        data-testid={`unban-button-${user.id}`}
                      >
                        Unban
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-full text-red-400"
                        onClick={() => handleBanUser(user.id, true)}
                        data-testid={`ban-button-${user.id}`}
                      >
                        Ban
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
